import React, { useState, useEffect, useRef } from "react";
import api from "../../hooks/api";
import { API_URLS } from "../../App";
import { Search } from "lucide-react";

export interface Product {
  id: string;
  name: string;
  short_description: string;
  brand: string;
  /** Component type from ERP TIPO feature (e.g. "Processador", "Placa Gráfica") */
  type?: string;
  categoryName?: string;
  cost_price: number;
  rev3_price: number;
  rev4_price: number;
  pvp_price: number;
  stock: number;
  is_base_component?: boolean;
}

/**
 * Maps the OMS "Categoria" (component slot type) to the Niposom catalog
 * rootCategoryId so searches in a slot are filtered to the right product family.
 */
export const COMPONENT_TYPE_TO_ROOT_CATEGORY_ID: Record<string, string> = {
  Processador: "222CPU",
  Motherboard: "222MBA",
  "Placa Gráfica": "222PGV",
  "Memória RAM": "222MEM",
  Armazenamento: "222HDD",
  "Fonte de Alimentação": "222PSU",
  Caixa: "222CAX",
  Cooler: "222DIS",
};

interface ProductSearchInputProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (product: Product) => void;
  placeholder?: string;
  className?: string;
  /**
   * When set, restricts product search to this component category
   * (e.g. "Processador", "Placa Gráfica"). Maps to rootCategoryId filter
   * on the POST /api/products/_search endpoint.
   */
  componentType?: string;
  /**
   * When true, searches against the all_products view (products + ERP fallback).
   */
  allProducts?: boolean;
}

const ProductSearchInput: React.FC<ProductSearchInputProps> = ({
  value,
  onChange,
  onSelect,
  placeholder = "",
  className = "",
  componentType,
  allProducts = false,
}) => {
  const [suggestions, setSuggestions] = useState<Product[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const updateDropdownPosition = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom, // For fixed positioning, use viewport coordinates directly
        left: rect.left,
        width: rect.width,
      });
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    const handleScroll = () => {
      updateDropdownPosition();
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, []);

  useEffect(() => {
    const timerId = setTimeout(() => {
      if (value.length >= 2) {
        fetchSuggestions(value);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 500); // Debounce time

    return () => {
      clearTimeout(timerId);
    };
  }, [value, componentType]);

  const fetchSuggestions = async (query: string) => {
    setLoading(true);
    try {
      const rootCategoryId = componentType
        ? COMPONENT_TYPE_TO_ROOT_CATEGORY_ID[componentType]
        : undefined;

      // Always use GET /compounds/products/_search — pass rootCategoryId as query param when filtering by slot
      const response = await api.get<{ results: Product[] }>(
        API_URLS.COMPOUNDS + "/products/_search",
        {
          params: {
            search: query,
            limit: 10,
            ...(rootCategoryId ? { rootCategoryId } : {}),
            ...(allProducts ? { allProducts: true } : {}),
          },
        },
      );
      const results: Product[] = response.data?.results ?? [];

      setSuggestions(results);
      setShowSuggestions(true);
    } catch (error) {
      console.error("Error fetching products:", error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (product: Product) => {
    onSelect(product);
    setShowSuggestions(false);
    // We might want to keep the value as the reference or name, depending on use case
    // For now, we let the parent handle the value update via onSelect -> parent state update -> value prop
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          className="w-full border rounded px-2 py-1 text-gray-900 pr-8"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          onFocus={() => {
            updateDropdownPosition();
            if (value.length >= 2) setShowSuggestions(true);
          }}
        />
        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400">
          {loading ? (
            <div className="animate-spin h-4 w-4 border-2 border-blue-500 rounded-full border-t-transparent"></div>
          ) : (
            <Search size={16} />
          )}
        </div>
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <ul
          className="fixed z-50 min-w-96 bg-white border border-gray-300 rounded mt-1 shadow-lg max-h-60 overflow-y-auto"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
          }}
        >
          {suggestions.map((product) => (
            <li
              key={product.id}
              className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
              onClick={() => handleSelect(product)}
            >
              <div className="font-medium text-gray-900">{product.id}</div>
              <div className="text-gray-500 text-xs truncate">
                {product.name}
              </div>
            </li>
          ))}
        </ul>
      )}
      {showSuggestions &&
        suggestions.length === 0 &&
        !loading &&
        value.length >= 2 && (
          <div
            className="fixed z-50 min-w-96 bg-white border border-gray-300 rounded mt-1 shadow-lg p-2 text-sm text-gray-500 text-center"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
            }}
          >
            Nenhum produto encontrado.
          </div>
        )}
    </div>
  );
};

export default ProductSearchInput;
